import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel,
  AlertDialogContent, AlertDialogDescription, AlertDialogFooter,
  AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger,
} from '../ui/alert-dialog';
import {
  FileText, Trash2, Edit, Loader2,
  Receipt, Presentation, Building2, FolderKanban,
} from 'lucide-react';
import { formatCurrency } from '../../lib/defaultData';
import { toast } from 'sonner';
import ProjectLinkCell from '../shared/ProjectLinkCell';
import { API } from '../../lib/api';
import axios from 'axios';
import { ExportDropdown } from '../shared/PageActions';
import { generateQuotePDF, computeQuoteFinancials } from '../../lib/pdfExport';
import { getDefaultCompanyInfo } from '../../lib/brandConfig';

export default function QuotesTab({ onEditQuote, onOpenProposal }) {
  const navigate = useNavigate();
  const [quotes, setQuotes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [creatingInvoiceForQuote, setCreatingInvoiceForQuote] = useState(null);
  const [projects, setProjects] = useState([]);

  // ── Fetch data ──────────────────────────────────────────────────────────────
  const fetchQuotes = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/quotes`);
      const sorted = [...(res.data.data || [])].sort((a, b) => {
        const ta = a.created_at ? new Date(a.created_at).getTime() : 0;
        const tb = b.created_at ? new Date(b.created_at).getTime() : 0;
        return tb - ta;
      });
      setQuotes(sorted);
    } catch (err) {
      console.error('Failed to fetch quotes:', err);
    }
  }, []);

  useEffect(() => {
    fetchQuotes().finally(() => setLoading(false));
  }, [fetchQuotes]);

  // ── Projects, for resolving project_id → name and for the assign picker ─────
  useEffect(() => {
    axios.get(`${API}/projects?limit=200`)
      .then(res => setProjects(res.data.data || []))
      .catch(() => {});
  }, []);

  const handleLinked = useCallback((quoteId, projectId) => {
    setQuotes(prev => prev.map(q => (q.id === quoteId ? { ...q, project_id: projectId } : q)));
  }, []);

  // ── New invoice from quote ──────────────────────────────────────────────────
  const handleNewInvoiceFromQuote = async (quote) => {
    if (creatingInvoiceForQuote) return;
    setCreatingInvoiceForQuote(quote.id);
    try {
      const [numRes, quoteRes] = await Promise.all([
        axios.get(`${API}/invoices/next-number`),
        axios.get(`${API}/quotes/${quote.id}`),
      ]);
      const invoiceNo = numRes.data.invoice_no;
      const fullQuote = quoteRes.data;
      const today = new Date().toISOString().slice(0, 10);
      const due = new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString().slice(0, 10);

      // Convert Quote categories → Invoice line items
      const categories = fullQuote.categories || [];
      const lineItems = categories.length > 0
        ? categories.map(cat => ({
            id: cat.id || crypto.randomUUID(),
            description: cat.name || 'Unnamed Category',
            amount: Number(cat.price) || 0,
          }))
        : [{ id: crypto.randomUUID(), description: '', amount: 0 }];

      const payload = {
        invoice_no: invoiceNo,
        invoice_date: today,
        due_date: due,
        quote_id: fullQuote.id,
        quote_no: fullQuote.quote_no || '',
        client_id: fullQuote.client_id || '',
        client_name: fullQuote.client_name || '',
        client_abn: fullQuote.client_abn || '',
        contact_person: fullQuote.contact_person || '',
        client_address: fullQuote.client_address || '',
        project_name: fullQuote.project_name || '',
        project_address: fullQuote.project_address || '',
        company_info: fullQuote.company_info || {},
        items: lineItems,
        gst_enabled: fullQuote.gst_enabled ?? true,
        notes: '',
        status: 'draft',
        payment_details: fullQuote.payment_details || {},
      };

      const createRes = await axios.post(`${API}/invoices`, payload);
      navigate(`/invoice/${createRes.data.id}`);
    } catch (err) {
      console.error('Failed to create invoice:', err);
    }
    setCreatingInvoiceForQuote(null);
  };

  // ── Delete ──────────────────────────────────────────────────────────────────
  const handleDeleteQuote = async (id) => {
    try {
      await axios.delete(`${API}/quotes/${id}`);
      setQuotes(prev => prev.filter(q => q.id !== id));
    } catch (err) {
      console.error('Failed to delete quote:', err);
    }
  };

  // ── Export ─────────────────────────────────────────────────────────────────
  const companyInfo = getDefaultCompanyInfo();

  const fetchFullQuote = async (id) => {
    try {
      const res = await axios.get(`${API}/quotes/${id}`);
      return res.data;
    } catch {
      return null;
    }
  };

  // generateQuotePDF is async — without await + catch a failure becomes an
  // unhandled rejection and the user sees nothing at all.
  const handleGeneratePdf = async (mode) => {
    if (quotes.length === 0) {
      toast.info('There are no quotations to export.');
      return;
    }
    const q = await fetchFullQuote(quotes[0].id);
    if (!q) {
      toast.error('Could not load that quotation to export.');
      return;
    }
    try {
      const { subtotal, gstAmount, total, paymentTotal, retentionPercentage } = computeQuoteFinancials(q);
      await generateQuotePDF(q, subtotal, gstAmount, total, paymentTotal, retentionPercentage, mode);
      if (mode === 'download') toast.success('PDF generated');
    } catch (err) {
      console.error('PDF generation failed:', err);
      toast.error('Failed to generate the PDF.');
    }
  };
  const handlePdfPreview = () => handleGeneratePdf('preview');
  const handlePdfDownload = () => handleGeneratePdf('download');
  const handlePrint = () => window.print();

  // ── Render ──────────────────────────────────────────────────────────────────
  if (loading) {
    return (
      <div className="text-center py-16 text-[#6b7280]">
        <Loader2 className="w-8 h-8 mx-auto mb-3 animate-spin opacity-40" />
        Loading...
      </div>
    );
  }

  return (
    <>
      <div className="flex items-center justify-between mb-4 sm:mb-6">
        <h2 className="text-base sm:text-xl font-semibold text-[#1a1a2e]" style={{ fontFamily: "'Space Grotesk', sans-serif" }}>
          All Quotations
        </h2>
        <div className="flex items-center gap-2">
          <ExportDropdown onPdfPreview={handlePdfPreview} onPdfDownload={handlePdfDownload} onPrint={handlePrint} />
        </div>
      </div>

      {quotes.length === 0 ? (
        <div className="text-center py-16">
          <FileText className="w-16 h-16 mx-auto text-[#c9a84c]/40 mb-4" />
          <p className="text-[#6b7280] text-lg mb-2">No quotations yet</p>
          <p className="text-[#6b7280]/60 text-sm">Quotes are created from inside a project</p>
        </div>
      ) : (
        <div className="space-y-1">
          <div
            className="hidden sm:grid gap-3 px-4 py-2 text-xs font-semibold uppercase tracking-wider text-[#6b7280]"
            style={{ gridTemplateColumns: '120px 100px 1fr 1fr 130px 140px' }}
          >
            <span>Quote No</span>
            <span>Date</span>
            <span>Client</span>
            <span>Project</span>
            <span className="text-right">Subtotal</span>
            <span className="text-right">Actions</span>
          </div>

          {quotes.map((q, idx) => (
            <div key={q.id} data-testid={`quote-row-${idx}`}>
              {/* Desktop row */}
              <div
                className="hidden sm:grid gap-3 px-4 py-3 rounded-md hover:bg-[#f3f2ef] transition-colors cursor-pointer items-center"
                style={{
                  gridTemplateColumns: '120px 100px 1fr 1fr 130px 140px',
                  borderBottom: '1px solid rgba(201,168,76,0.1)',
                }}
                onClick={() => onEditQuote(q.id)}
              >
                <span className="font-mono font-semibold text-sm text-[#1a1a2e]">{q.quote_no || '—'}</span>
                <span className="text-sm text-[#6b7280]">{q.date || '—'}</span>
                <span className="text-sm truncate">
                  {q.client_name ? (
                    <span className="flex items-center gap-1">
                      {q.client_name}
                      {q.client_id && (
                        <button
                          onClick={(e) => { e.stopPropagation(); navigate(`/clients/${q.client_id}`); }}
                          className="inline-flex items-center p-0.5 rounded hover:bg-[#c9a84c]/10 text-[#9ca3af] hover:text-[#c9a84c] transition-colors"
                          title="View client"
                        >
                          <Building2 className="w-3 h-3" />
                        </button>
                      )}
                    </span>
                  ) : '—'}
                </span>
                <ProjectLinkCell
                  docId={q.id}
                  collection="quotes"
                  projectId={q.project_id}
                  fallbackText={q.project_name}
                  projects={projects}
                  onLinked={handleLinked}
                />
                <span className="text-sm font-mono text-right font-medium">
                  {q.subtotal != null ? formatCurrency(q.subtotal) : '—'}
                </span>
                <div className="flex items-center justify-end gap-1" onClick={e => e.stopPropagation()}>
                  <button onClick={() => onOpenProposal(q.id)} className="p-1.5 rounded hover:bg-[#c9a84c]/10 text-[#6b7280] hover:text-[#c9a84c] transition-colors" title="Open Proposal Pages"><Presentation className="w-4 h-4" /></button>
                  <button onClick={() => onEditQuote(q.id)} className="p-1.5 rounded hover:bg-[#c9a84c]/10 text-[#6b7280] hover:text-[#1a1a2e] transition-colors" title="Edit Quotation"><Edit className="w-4 h-4" /></button>
                  <button onClick={() => handleNewInvoiceFromQuote(q)} disabled={creatingInvoiceForQuote === q.id} className="p-1.5 rounded hover:bg-blue-50 text-[#6b7280] hover:text-blue-600 transition-colors disabled:opacity-40" title="Create Invoice">{creatingInvoiceForQuote === q.id ? <Loader2 className="w-4 h-4 animate-spin" /> : <Receipt className="w-4 h-4" />}</button>
                  {q.project_id && (
                    <button
                      onClick={() => navigate(`/projects/${q.project_id}`)}
                      className="p-1.5 rounded transition-colors text-emerald-600 hover:bg-emerald-50"
                      title="View Project"
                    >
                      <FolderKanban className="w-4 h-4" />
                    </button>
                  )}
                  <AlertDialog>
                    <AlertDialogTrigger asChild><button className="p-1.5 rounded hover:bg-red-50 text-[#6b7280] hover:text-[#dc2626] transition-colors"><Trash2 className="w-4 h-4" /></button></AlertDialogTrigger>
                    <AlertDialogContent>
                      <AlertDialogHeader><AlertDialogTitle>Delete Quotation</AlertDialogTitle><AlertDialogDescription>Are you sure you want to delete {q.quote_no || 'this quote'}? This action cannot be undone.</AlertDialogDescription></AlertDialogHeader>
                      <AlertDialogFooter><AlertDialogCancel>Cancel</AlertDialogCancel><AlertDialogAction onClick={() => handleDeleteQuote(q.id)} className="bg-[#dc2626] hover:bg-[#b91c1c]">Delete</AlertDialogAction></AlertDialogFooter>
                    </AlertDialogContent>
                  </AlertDialog>
                </div>
              </div>

              {/* Mobile card */}
              <div
                className="sm:hidden px-3 py-3 cursor-pointer hover:bg-[#f3f2ef] transition-colors rounded-md"
                style={{ borderBottom: '1px solid rgba(201,168,76,0.12)' }}
                onClick={() => onEditQuote(q.id)}
              >
                <div className="flex justify-between items-start">
                  <span className="font-mono font-semibold text-sm text-[#1a1a2e]">{q.quote_no || '—'}</span>
                  <span className="font-mono text-sm font-semibold text-[#1a1a2e]">
                    {q.subtotal != null ? formatCurrency(q.subtotal) : '—'}
                  </span>
                </div>
                <div className="text-sm font-medium text-[#1a1a2e] mt-0.5">{q.client_name || '—'}</div>
                <div className="text-xs text-[#6b7280] truncate">{q.project_name || '—'}</div>
                <div className="flex justify-between items-center mt-2">
                  <span className="text-xs text-[#9ca3af]">{q.date || '—'}</span>
                  <div className="flex gap-1" onClick={e => e.stopPropagation()}>
                    <button onClick={() => onOpenProposal(q.id)} className="p-2 rounded hover:bg-[#c9a84c]/10 text-[#6b7280] hover:text-[#c9a84c] transition-colors" title="Open Proposal Pages"><Presentation className="w-4 h-4" /></button>
                    <button onClick={() => onEditQuote(q.id)} className="p-2 rounded hover:bg-[#c9a84c]/10 text-[#6b7280] hover:text-[#1a1a2e] transition-colors" title="Edit"><Edit className="w-4 h-4" /></button>
                    <button onClick={() => handleNewInvoiceFromQuote(q)} disabled={creatingInvoiceForQuote === q.id} className="p-2 rounded hover:bg-blue-50 text-[#6b7280] hover:text-blue-600 transition-colors disabled:opacity-40" title="Create Invoice">{creatingInvoiceForQuote === q.id ? <Loader2 className="w-4 h-4 animate-spin" /> : <Receipt className="w-4 h-4" />}</button>
                    {q.project_id && (
                      <button
                        onClick={() => navigate(`/projects/${q.project_id}`)}
                        className="p-2 rounded transition-colors text-emerald-600 hover:bg-emerald-50"
                        title="View Project"
                      >
                        <FolderKanban className="w-4 h-4" />
                      </button>
                    )}
                    <AlertDialog>
                      <AlertDialogTrigger asChild><button className="p-2 rounded hover:bg-red-50 text-[#6b7280] hover:text-[#dc2626] transition-colors"><Trash2 className="w-4 h-4" /></button></AlertDialogTrigger>
                      <AlertDialogContent>
                        <AlertDialogHeader><AlertDialogTitle>Delete Quotation</AlertDialogTitle><AlertDialogDescription>Are you sure you want to delete {q.quote_no || 'this quote'}? This action cannot be undone.</AlertDialogDescription></AlertDialogHeader>
                        <AlertDialogFooter><AlertDialogCancel>Cancel</AlertDialogCancel><AlertDialogAction onClick={() => handleDeleteQuote(q.id)} className="bg-[#dc2626] hover:bg-[#b91c1c]">Delete</AlertDialogAction></AlertDialogFooter>
                      </AlertDialogContent>
                    </AlertDialog>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </>
  );
}
